import { useEffect, useState, useRef, useContext, useMemo } from "react";
import Head from "next/head";
import Modal from "react-modal";
import Page from "../layouts/main";
import CTA from "../components/CTA/cta.module";
import Footer from "../components/Footer/footer.module";
import Picture from "../components/picture/picture.module";
import Video from "../components/video/video.module";
import Context from "../components/context/context";
import styles from "../styles/modules/how-it-works.module.scss";
import settings from "../util/settings";
import { DEFAULT_LOCATION, formatAddress } from "../util/locations";

const houseRules = [
  "You must bring your own clubs. Ensure your clubs are clean for precise club head data.",
  "Only use our specially marked balls, as this will give you the most accurate data.",
  "No smoking or vaping inside the facility – our technology allows you to exit and re-enter the facility during your time slot if necessary.",
  "Zero tolerance alcohol consumption policy on premises.",
  "Clean shoes must be worn on the hitting area.",
  "Keep all food and drinks away from the hitting area.",
  "Please keep the area clean and tidy for the enjoyment of all.",
];

const HowItWorks = () => {
  const [analytics, setAnalytics] = useState();
  const [modal, setModal] = useState({ open: false, videoURL: "" });
  const page = useRef();

  const { location } = useContext(Context);
  const locationToUse = useMemo(
    () => (location ? location : DEFAULT_LOCATION),
    [location]
  );
  const formattedAddress = useMemo(
    () => formatAddress(locationToUse.address),
    [locationToUse]
  );

  const toggleModal = (state, videoURL) => {
    setModal({
      open: state === "on",
      videoURL: videoURL,
    });
  };

  useEffect(() => {
    if (!window.location.hash) window.scrollTo(0, 0);
    if (page.current) {
      setAnalytics(page.current.analytics);
    }
  }, []);

  return (
    <Page id={styles["page-how-it-works"]} ref={page}>
      <Head>
        <title>How It Works - Kudos Golf</title>
      </Head>

      <Modal isOpen={modal.open}>
        <button className="close-button" onClick={() => toggleModal("off")}>
          &#10005;
        </button>
        <Video
          url={modal.videoURL}
          controls={true}
          playing={true}
          analytics={analytics}
        />
      </Modal>

      <section className={`section-content ${styles["section-header"]}`}>
        <h2 className={styles.header}>
          Book your session and get ready to experience the best of golf.
        </h2>
      </section>

      <section className={`section-content ${styles["section-how-it-works"]}`}>
        <div className={`row ${styles["step-container"]}`}>
          <div className="col-md-5">
            <h3 className={styles["step-number"]}>
              Step One
              <br />
              <span className={styles["step-title"]}>Booking a tee time</span>
            </h3>
          </div>
          <div className="col-md-6">
            <p>
              Use our booking system to view available time slots. Select the
              day, time, and type of bay that you would like to book.
            </p>
            <p>
              Time slots are available for booking up to 5 days in advance.
              Same-day bookings are permitted, but you will need to book at
              least 30 minutes in advance to avoid delays. For private
              tournaments and event bookings, please&nbsp;
              <CTA
                href="/contact-us"
                className={"text-link"}
                text="contact us"
                analytics={analytics}
                src="HOW-IT-WORKS"
                dest="/contact-us"
              />{" "}
              .
            </p>
            <p>
              Once you’ve paid for your booking, you will receive an email
              confirmation with your tee time. Within 15 minutes of making your
              booking. you will also receive an email with your Smart Door
              Access Key, which you will need to enter the facility.
            </p>
            <p>
              Kindly watch the video tutorial below for assistance with booking
              a time slot.
            </p>
          </div>
        </div>

        <span id="booking-tutorial">
          <Video
            url={`${settings.ASSET_PATH}videos/acuity-booking-tutorial.mp4`}
            controls={true}
            playing={true}
            preview={`${settings.ASSET_PATH}videos/thumbnail/acuity-booking-tutorial.jpg`}
            analytics={analytics}
          />
        </span>

        <div className={`row ${styles["step-container"]}`}>
          <div className="col-md-5">
            <h3 className={styles["step-number"]}>
              Step Two
              <br />
              <span className={styles["step-title"]}>Using the facility</span>
            </h3>
          </div>
          <div className="col-md-6">
            <p>
              You will be able to access the facility 5 minutes before your
              scheduled time – plan to arrive early. Enter the unit using the
              Smart Door Access Key (sent to you via email), and go to your
              selected bay. There is wifi available at the entrance, but you
              will need a smartphone to access your email.
            </p>
            <p>
              All golfers will need to bring their own golf clubs. Please ONLY
              use the unique marked balls at each hitting bay.
            </p>
            <p>
              For first-time players, please watch the tutorials outlining how
              to use our&nbsp;
              <CTA
                className={"text-link"}
                text="Driving Range"
                analytics={analytics}
                src="HOW-IT-WORKS"
                dest="kudos-golf-driving-range.mp4"
                onClick={() =>
                  toggleModal(
                    "on",
                    `${settings.ASSET_PATH}videos/kudos-golf-driving-range.mp4`
                  )
                }
              />
              &nbsp;and&nbsp;
              <CTA
                className={"text-link"}
                text="Stroke Play"
                analytics={analytics}
                src="HOW-IT-WORKS"
                dest="kudos-golf-how-to-play.mp4"
                onClick={() =>
                  toggleModal(
                    "on",
                    `${settings.ASSET_PATH}videos/kudos-golf-how-to-play.mp4`
                  )
                }
              />
              . Prior to your session, you can&nbsp;
              <CTA
                href="https://portal.e6golf.com/signup"
                className={"text-link"}
                text="sign up"
                target="_blank"
                analytics={analytics}
                src="HOW-IT-WORKS"
                dest="https://portal.e6golf.com/signup"
              />
              &nbsp;for E6 which will grant you access to their golf portal,
              allowing you to track and review your personal golf statistics.
              You can also&nbsp;
              <CTA
                className={"text-link"}
                text="create a player profile"
                analytics={analytics}
                src="HOW-IT-WORKS"
                dest="kudos-golf-create-profile.mp4"
                onClick={() =>
                  toggleModal(
                    "on",
                    `${settings.ASSET_PATH}videos/kudos-golf-create-profile.mp4`
                  )
                }
              />
              &nbsp;during your session at Kudos.
            </p>
            <p>
              Please be sure to follow the house rules outlined below. If you
              need support during your session, please feel free to contact us
              at&nbsp;
              <CTA
                href={`mailto:${locationToUse.email}`}
                className={"text-link"}
                text={locationToUse.email}
                analytics={analytics}
                src="HOW-IT-WORKS"
                dest={locationToUse.email}
              />{" "}
              – enjoy your golf training session!
            </p>
          </div>
        </div>
      </section>

      <section className={`section-content ${styles["section-floor-plan"]}`}>
        <h3 className={styles["section-floor-plan-heading"]}>
          Facility Floorplan
        </h3>

        <Picture
          src={settings.ASSET_PATH + locationToUse.floorplan.image}
          className={styles["floorplan-image"]}
          alt={`Kudos Golf ${formattedAddress.city} floor plan`}
          title={`Kudos Golf ${formattedAddress.city} floor plan`}
        />

        <div className={styles["floor-plan-options-container"]}>
          {locationToUse.floorplan.privateBays && (
            <div className={styles["section-floor-plan-option"]}>
              <p className={`text-body-small ${styles["option-title"]}`}>
                <span
                  className={`${styles["circle"]} ${styles["circle-orange"]}`}
                ></span>
                Private Bays
                {locationToUse.floorplan.privateBaysConnected && " (connected)"}
              </p>
            </div>
          )}
          {locationToUse.floorplan.openBays && (
            <div className={`${styles["section-floor-plan-option"]}`}>
              <p className={`text-body-small ${styles["option-title"]}`}>
                <span
                  className={`${styles["circle"]} ${styles["circle-green"]}`}
                ></span>
                Open Bays
              </p>
            </div>
          )}
        </div>

        <span id="facility-access-tutorial">
          <Video
            url={`${settings.ASSET_PATH}videos/kudos-golf-accessing-the-facility.mp4`}
            controls={true}
            playing={true}
            preview={`${settings.ASSET_PATH}videos/thumbnail/kudos-golf-accessing-the-facility.jpg`}
            analytics={analytics}
          />
        </span>
      </section>

      <section
        id="house-rules"
        className={`section-content ${styles["section-house-rules"]}`}
      >
        <h2 className={styles["house-rules-title"]}>House Rules</h2>
        <ol className={`row ${styles["house-rules-list"]}`}>
          <div className="col-md-5">
            {houseRules
              .slice(0, Math.ceil(houseRules.length / 2))
              .map((rule, index) => (
                <li key={index}>{rule}</li>
              ))}
          </div>
          <div className="col-md-5 offset-md-1">
            {houseRules
              .slice(Math.ceil(houseRules.length / 2))
              .map((rule, index) => (
                <li key={index}>{rule}</li>
              ))}
          </div>
        </ol>
      </section>

      <Footer />
    </Page>
  );
};

export default HowItWorks;
